import { useState } from 'react'
import { applyAvailability, commonAvailability, dayLabel, requestWindowLabel } from '@kronos/domain'
import type { AvailabilityStatus, DayAvailability, LocalDate, TimeWindow } from '@kronos/domain'
import type { ComeInRequestView } from '../../data/schedule'
import { problemOf } from '../hours/time'
import { Modal } from '../kit'
import { Badge } from '../primitives'
import { STATE_LABEL, STATE_TONE, useSaveAvailability } from './shared'

type Choice = AvailabilityStatus | null

const CHOICES: readonly { value: Choice; label: string }[] = [
  { value: 'available', label: 'Available' },
  { value: 'unavailable', label: 'Off' },
  { value: null, label: 'Clear' },
]

/**
 * One day, or several, opened from the calendar: Available, Off or Clear as
 * three big buttons, the hours when available, and a note. On a phone it rises
 * from the bottom of the screen, where the thumb already is.
 *
 * Several days open on what they share (`commonAvailability`). Their notes are
 * left alone unless one is typed — "make these four available" must not wipe
 * the "dentist pm" on one of them (`applyAvailability`).
 */
export function DayEditor({
  dates,
  mine,
  requests,
  window: fallback,
  today,
  timeZone,
  onClose,
  onSaved,
}: {
  dates: readonly LocalDate[]
  mine: readonly DayAvailability[]
  /** What the team has asked for on these days. */
  requests: readonly ComeInRequestView[]
  /** The hours to offer when the days have none of their own. */
  window: TimeWindow
  today: LocalDate
  timeZone: string
  onClose: () => void
  onSaved: () => void
}): JSX.Element {
  const common = commonAvailability(dates, mine)
  const many = dates.length > 1
  const [status, setStatus] = useState<Choice>(common.status === 'mixed' || common.status === null ? 'available' : common.status)
  const shared = common.window && common.window !== 'mixed' ? common.window : fallback
  const [from, setFrom] = useState<string>(shared.from)
  const [to, setTo] = useState<string>(shared.to)
  const [note, setNote] = useState(typeof common.note === 'string' ? common.note : '')
  const [problem, setProblem] = useState('')
  const save = useSaveAvailability((e) => setProblem(problemOf(e)))

  const submit = (): void => {
    setProblem('')
    const keepNotes = many && common.note === 'mixed' && note.trim() === ''
    const edits = applyAvailability(dates, { status, window: { from, to }, ...(keepNotes ? {} : { note }) }, mine, today)
    if (edits.length === 0) return onClose()
    save.mutate(edits, { onSuccess: onSaved })
  }

  const title = many ? `${dates.length} days` : `${dayLabel(dates[0] as LocalDate)}${dates[0] === today ? ' · Today' : ''}`
  const subtitle = many ? spanLabel(dates) : 'When can you come in?'

  return (
    <Modal
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          {problem ? (
            <span className="critical" role="alert">
              {problem}
            </span>
          ) : null}
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={save.isPending} onClick={submit}>
            {many ? `Set ${dates.length} days` : 'Save'}
          </button>
        </>
      }
    >
      <div className="avail-sheet schedule-form">
        <div className="avail-choice" role="group" aria-label="Availability">
          {CHOICES.map((c) => (
            <button key={c.label} type="button" className={`avail-choice__${c.value ?? 'clear'}`} aria-pressed={status === c.value} onClick={() => setStatus(c.value)}>
              {c.label}
            </button>
          ))}
        </div>
        {common.status === 'mixed' ? <span className="muted avail-sheet__hint">These days are set differently now. Saving sets them all the same.</span> : null}

        {status === 'available' ? (
          <div className="avail-sheet__times">
            <label className="field">
              <span className="field-label">From</span>
              <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="field">
              <span className="field-label">To</span>
              <input type="time" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
        ) : null}

        {status !== null ? (
          <label className="field">
            <span className="field-label">Note (optional)</span>
            <input
              maxLength={140}
              placeholder={many && common.note === 'mixed' ? 'Leave empty to keep each day’s own note' : status === 'available' ? 'e.g. Leaving at 3' : 'e.g. Away'}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
        ) : (
          <span className="muted avail-sheet__hint">{many ? 'These days' : 'This day'} will show as not set.</span>
        )}

        {requests.length > 0 ? (
          <div className="avail-sheet__asked">
            <span className="field-label">Asked by the team</span>
            {requests.map((r) => (
              <span key={r.id} className="avail-sheet__request">
                <Badge tone={STATE_TONE[r.state]}>{STATE_LABEL[r.state]}</Badge>
                <span className="figure">{requestWindowLabel(r, timeZone)}</span>
                <span className="muted">{r.note || `asked by ${r.requestedByName}`}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

/** `Mon 26 Oct – Fri 30 Oct`, or the first few and a count. */
function spanLabel(dates: readonly LocalDate[]): string {
  const sorted = [...dates].sort()
  if (sorted.length <= 3) return sorted.map(dayLabel).join(', ')
  return `${dayLabel(sorted[0] as LocalDate)} – ${dayLabel(sorted[sorted.length - 1] as LocalDate)}`
}
